import React, { useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './ContentPageHeader.module.scss';
import { Icon } from '../../components/Icon';
import { useLibraryContext } from '../../context/LibraryContext';
import { CustomButton } from '../Button/Button';
import { useTranslation } from 'react-i18next';
import UsersList from '../UsersList/UsersList';
import { useUserContext } from '../../context/UserContext';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import Tooltip from '../Tooltip/Tooltip';
import SearchComponentWithTags from '../SearchComponentWithTags/SearchComponentWithTags';
import SearchComponent from '../SearchComponent/SearchComponent';

const ContentPageHeader = ({
  userFullName,
  isListView,
  handleSetGridView,
  handleSetListView,
  handleInputChange,
  showShareFolderModal,
  searchValue,
  title,
  isFolderEditable,
  folderUsers,
  mode,
  isVideosTab,
  handleCreateNewFolder,
  className,
  isNewVideosAllowed = true,
}) => {
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const { appConfig } = useUserContext();

  const { enableShowRecordingActions } = useLibraryContext();

  const [isTitleHover, setIsTitleHover] = useState(false);

  const titleRef = useRef();

  const isTitleEllipsis = useMemo(() => {
    const deviation = 5; // value in px
    return titleRef.current?.scrollHeight - titleRef.current?.offsetHeight > deviation;
  }, [titleRef.current?.scrollHeight]);

  const { t } = useTranslation();

  return (
    <div className={cx(styles.header, { [styles.libraryMode]: mode === 'LIBRARY' }, className)}>
      <div className={styles.titleContainer}>
        {title && (
          <h1
            ref={titleRef}
            className={styles.title}
            onMouseEnter={() => setIsTitleHover(true)}
            onMouseLeave={() => setIsTitleHover(false)}
            tabIndex={0}
          >
            {title}
          </h1>
        )}
        {isTitleEllipsis && <Tooltip text={title} isHover={isTitleHover} moveable={false} classNameTooltip={styles.fullTitle} />}
        {(mode === 'LIBRARY' || mode === 'ARCHIVE') && (
          <h2 tabIndex={0}>
            {userFullName}
            {appConfig?.name ? ' / ' + appConfig.name : ''}
          </h2>
        )}
        {mode === 'FOLDER' && folderUsers && <UsersList isFolderEditable={isFolderEditable} users={folderUsers} />}
      </div>
      <div className={styles.searchIconsWrapper}>
        {!isMobileView && (
          <>
            {(mode !== 'ARCHIVE' && isVideosTab) ?
              <SearchComponentWithTags searchValue={searchValue} onChange={handleInputChange} clearButton maxLength={MAX_NAME_LENGTH} />
              :
              <SearchComponent searchValue={searchValue.query} onChange={handleInputChange} clearButton maxLength={MAX_NAME_LENGTH} />
            }
          </>
        )}
        {!isMobileView && mode === 'FOLDER' && showShareFolderModal && (
          <CustomButton variant="link" className={styles.shareFolderBtn} onClick={showShareFolderModal} ariaLabel={t('general.video_action_button.share')}>
            <Icon src="shareFolder" className={styles.shareFolderIcon} />
          </CustomButton>
        )}
        {!isMobileView && mode === 'LIBRARY' && isVideosTab && (
          <CustomButton className={styles.addFolderIconBtn} onClick={handleCreateNewFolder} variant="link" ariaLabel={`${t('general.add')} ${t('general.right_sidebar.folder')}`}>
            <Icon src="addFolder" className={styles.addFolderIcon} />
          </CustomButton>
        )}
        {isMobileView && mode === 'LIBRARY' && (
          <CustomButton variant="link" className={styles.addFolderIconBtn} onClick={handleCreateNewFolder} ariaLabel={`${t('general.add')} ${t('general.right_sidebar.folder')}`}>
            <Icon src="addFolder" className={styles.addFolderIcon} />
          </CustomButton>
        )}
        <div className={styles.iconsContainer}>
          {!isMobileView && (mode === 'FOLDER' || mode === 'REVIEW' || isVideosTab) && (
            <div className={styles.iconsWrapper}>
              <CustomButton
                variant="link"
                className={cx(styles.iconsBtn, { [styles.active]: !isListView })}
                onClick={handleSetGridView}
                ariaLabel="Grid View Mode"
              >
                <Icon src="grid_view" className={styles.iconsGridList} />
              </CustomButton>
              <CustomButton
                variant="link"
                className={cx(styles.iconsBtn, { [styles.active]: isListView })}
                onClick={handleSetListView}
                ariaLabel="List View Mode"
              >
                <Icon src="list_view" className={styles.iconsGridList} />
              </CustomButton>
            </div>
          )}
          {!isMobileView && (mode === 'LIBRARY' || mode === 'FOLDER') && (
            <>
              {handleCreateNewFolder && !isVideosTab && (
                <CustomButton
                  label={t('dashboard.new_folder.button')}
                  variant="secondary"
                  className={styles.addFolderBtn}
                  onClick={handleCreateNewFolder}
                />
              )}
              {isNewVideosAllowed && <CustomButton
                label={t('dashboard.new_video.button')}
                variant="basic"
                onClick={enableShowRecordingActions}
                className={styles.addVideoBtn}
              />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentPageHeader;
